import { useState, useCallback, useEffect } from "react";
import { LogisticItemService } from "../services/LogisticItemService";
import { LogisticCategoryService } from "../services/LogisticCategoryService";

export const useHookLogisticMonitoring = () => {
    // --- State untuk Tabel Barang ---
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // --- State untuk Filter & Pagination ---
    const [params, setParams] = useState({
        keyword: "",
        category_id: "",
        stock_status: "", // "critical" | "empty" | ""
        page: 1,
        per_page: 10,
    });

    // --- State untuk Kartu Statistik (Total, Terpakai, dll) ---
    const [stats, setStats] = useState({
        total_stock: 0,
        used: 0,
        unused: 0,
        damaged: 0,
        critical: 0,
        empty: 0,
    });

    // --- State untuk Data Peringatan Stok (Bagian "Peringatan Stok!") ---
    const [warnings, setWarnings] = useState([]);

    // --- State untuk Dropdown Kategori ---
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);

    /* ============================================
       FETCH FUNCTIONS
       ============================================ */

    // 1. Fetch Data Monitoring (Stats + Tabel)
    const getMonitoringData = useCallback(async () => {
        setLoading(true);
        setError(null);

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
        );

        try {
            const res = await LogisticItemService.getMonitoring({ params: cleanParams });
            const resData = res?.data;

            // Set Statistik untuk Kartu
            if (resData?.stats) {
                setStats(resData.stats);
            }

            // Set Data Tabel
            const result = resData?.data ?? [];
            setData(Array.isArray(result) ? result : []);
            
            // Set Meta Paginasi
            setMeta(resData?.meta ?? null);

            // Set Peringatan Stok (Item yang kritis atau habis)
            const items = Array.isArray(result) ? result : [];
            const criticalItems = items.filter(
                (item) => 
                    (item.unused_stock < item.min_stock && item.unused_stock > 0) || 
                    item.unused_stock <= 0
            );
            setWarnings(criticalItems);

        } catch (err) {
            console.error("Gagal memuat data monitoring:", err);
            setError(err?.response?.data || err);
        } finally {
            setLoading(false);
        }
    }, [params]);

    // 2. Fetch Kategori untuk Dropdown Filter
    const getCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const res = await LogisticCategoryService.get({ params: {} });
            setCategories(res?.data?.data ?? []);
        } catch (err) {
            console.error("Gagal memuat kategori:", err);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    /* ============================================
       ACTION FUNCTIONS
       ============================================ */

    const handleDeleteItem = useCallback(async (id) => {
        try {
            await LogisticItemService.destroy({ id });
            // Refresh data setelah berhasil hapus
            await getMonitoringData();
            return true;
        } catch (err) {
            throw err; // Lempar error agar bisa ditangkap UI (untuk Toast)
        }
    }, [getMonitoringData]);

    /* ============================================
       EFFECTS
       ============================================ */

    // Load Kategori saat pertama kali mount
    useEffect(() => {
        getCategories();
    }, [getCategories]);

    // Load Data Monitoring saat parameter berubah (keyword, filter, page)
    useEffect(() => {
        getMonitoringData();
    }, [getMonitoringData]);

    /* ============================================
       RETURN VALUES
       ============================================ */

    return {
        // Data Tabel & Paginasi
        data,
        meta,
        loading,
        error,
        params,
        setParams,
        refreshData: getMonitoringData,

        // Statistik Kartu
        stats,

        // Peringatan Stok
        warnings,

        // Dropdown Kategori
        categories,
        loadingCategories,

        // Aksi
        handleDeleteItem,
    };
};